// =============================================================================
// Event Timeline — compact, mobile-friendly chronological event list
// =============================================================================

import { useState } from 'react';
import { Badge, Button } from '@tkpod/ui';

interface OrderEvent {
  id: string;
  type: string;
  data: Record<string, unknown> | null;
  userId: string | null;
  userName: string | null;
  createdAt: string;
}

function s(v: unknown): string {
  return v == null ? '' : String(v);
}

const INITIAL_DISPLAY_COUNT = 20;
const LOAD_MORE_COUNT = 20;

interface EventTimelineProps {
  events: OrderEvent[];
}

const EVENT_LABELS: Record<string, string> = {
  ORDER_CREATED: 'Order Created',
  STATUS_CHANGED: 'Status Changed',
  PROVIDER_OVERRIDDEN: 'Provider Overridden',
  PROVIDER_ASSIGNED: 'Provider Assigned',
  PROVIDER_FAILED: 'Provider Issue',
  ON_HOLD: 'Put on Hold',
  HOLD_RELEASED: 'Released from Hold',
  FRAUD_HOLD: 'Fraud Hold',
  FRAUD_CLEARED: 'Fraud Hold Cleared',
  CANCELLED: 'Cancelled',
  CANCEL_REQUESTED: 'Cancel Requested',
  NOTE_ADDED: 'Note Added',
  NOTE_UPDATED: 'Note Updated',
  TAG_ADDED: 'Tag Added',
  TAG_REMOVED: 'Tag Removed',
  SHIPMENT_CREATED: 'Shipment Created',
  SHIPMENT_UPDATED: 'Shipment Updated',
  SHIPMENT_CANCELLED: 'Shipment Cancelled',
  SHIPMENT_DELIVERED: 'Shipment Delivered',
  TRACKING_UPDATED: 'Tracking Updated',
  SHIPPED: 'Order Shipped',
  DELIVERED: 'Order Delivered',
  ITEM_CANCELLED: 'Items Cancelled',
  PRODUCTION_STARTED: 'Production Started',
  PRODUCTION_COMPLETED: 'Production Completed',
  PARTIAL_SHIPPED: 'Partially Shipped',
  DESIGN_ATTACHED: 'Design Attached',
  EXPORT_STARTED: 'Export Started',
  EXPORT_COMPLETED: 'Export Completed',
  EXPORT_FAILED: 'Export Failed',
  EXPORT_REGENERATED: 'Exports Regenerated',
  RETURN_REQUESTED: 'Return Requested',
  RETURN_APPROVED: 'Return Approved',
  GDPR_PROVIDER_NOTIFY_REQUIRED: 'GDPR: Provider Notice Required',
  PROVIDER_SUBMITTED: 'Sent to provider',
};

function pickupEventLabel(event: OrderEvent): string | null {
  const kind = event.data?.kind;
  const outcome = event.data?.outcome;
  if (typeof kind !== 'string') return null;
  if (
    event.type === 'SHOPIFY_SYNCED' &&
    outcome === 'already_closed' &&
    (kind === 'PICKUP_READY' || kind === 'PICKUP_FULFILLED')
  ) {
    return 'Shopify pickup already closed — customer was not notified again';
  }
  if (event.type === 'SHOPIFY_SYNCED' && kind === 'PICKUP_READY') {
    return 'Ready for pickup — customer notified';
  }
  if (event.type === 'SHOPIFY_SYNCED' && kind === 'PICKUP_FULFILLED') {
    return 'Picked up — customer notified';
  }
  if (event.type === 'SHOPIFY_SYNC_FAILED' && kind.startsWith('PICKUP_')) {
    return 'Shopify pickup notification failed';
  }
  return null;
}

function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function getDateGroupLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86_400_000;
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (eventDay === today) return 'Today';
  if (eventDay === yesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function record(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function ShipmentEventDetail({ data }: { data: Record<string, unknown> }) {
  const previous = record(data.previous);
  const current = record(data.current);
  const carrier = data.carrier ?? current?.carrier;
  const trackingNumber = data.trackingNumber ?? current?.trackingNumber;
  const previousUrl =
    typeof previous?.labelUrl === 'string' ? previous.labelUrl : '';

  return (
    <div className="mt-1 rounded-md border border-blue-200/50 bg-blue-50/30 dark:bg-blue-500/5 px-2.5 py-1.5 text-[11px] space-y-0.5">
      {carrier != null && <p>Carrier: <span className="font-medium">{s(carrier)}</span></p>}
      {trackingNumber != null && <p>Tracking: <span className="font-mono font-medium">{s(trackingNumber)}</span></p>}
      {previous?.trackingNumber != null && (
        <p>Previous tracking: <span className="font-mono font-medium">{s(previous.trackingNumber)}</span></p>
      )}
      {previousUrl && (
        <p>
          <a
            href={previousUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-primary hover:underline"
          >
            View previous label
          </a>
        </p>
      )}
      {data.newStatus != null && <p>Status: <Badge variant="default" size="sm">{s(data.newStatus).replace(/_/g, ' ')}</Badge></p>}
      {data.itemCount != null && <p>Items: {s(data.itemCount)}</p>}
    </div>
  );
}

export function EventTimeline({ events }: EventTimelineProps) {
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  if (events.length === 0) {
    return (
      <p className="py-3 text-center text-xs text-text-tertiary">
        No events recorded.
      </p>
    );
  }

  const visibleEvents = events.slice(0, displayCount);
  const hasMore = events.length > displayCount;

  const groups = new Map<string, OrderEvent[]>();
  for (const event of visibleEvents) {
    const label = getDateGroupLabel(event.createdAt);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(event);
  }

  return (
    <div className="space-y-4">
      {Array.from(groups.entries()).map(([dateLabel, groupEvents]) => (
        <div key={dateLabel}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-2">
            {dateLabel}
          </p>
          <div className="relative pl-4">
            <div className="absolute left-[5px] top-1 bottom-1 w-px bg-border" />
            <div className="space-y-3">
              {groupEvents.map((event) => {
                const d = event.data as Record<string, unknown> | null;
                const isNote = event.type === 'NOTE_ADDED' || event.type === 'NOTE_UPDATED';
                const isProviderOverride = event.type === 'PROVIDER_OVERRIDDEN';
                const isShipmentEvent = event.type.startsWith('SHIPMENT_') || event.type === 'TRACKING_UPDATED';
                const isProviderFailed = event.type === 'PROVIDER_FAILED';
                const isItemCancelled = event.type === 'ITEM_CANCELLED';
                return (
                  <div key={event.id} className="relative">
                    <div className={`absolute -left-4 top-[5px] h-[7px] w-[7px] rounded-full border-2 ${
                      isProviderOverride ? 'border-orange-400 bg-orange-100' :
                      isProviderFailed ? 'border-red-400 bg-red-100' :
                      isShipmentEvent ? 'border-blue-400 bg-blue-100' :
                      isItemCancelled ? 'border-red-400 bg-red-100' :
                      event.type === 'SHIPPED' || event.type === 'DELIVERED' ? 'border-green-400 bg-green-100' :
                      'border-border bg-surface'
                    }`} />
                    <div className="space-y-0.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs font-medium text-text-primary">
                          {pickupEventLabel(event) ??
                          (isNote && d?.body
                            ? 'You added a note to this order.'
                            : event.type === 'PROVIDER_SUBMITTED'
                              ? (d?.action === 'PULL_CLAIMED'
                                  ? 'Provider claimed order'
                                  : d?.action === 'SELF_CLAIM'
                                    ? 'Provider claimed self-fulfilled lines'
                                    : d?.action === 'SEND_TO_PROVIDER'
                                      ? 'Sent self-fulfilled lines to provider'
                                      : 'Sent to provider')
                            : EVENT_LABELS[event.type] ?? event.type.replace(/_/g, ' '))}
                        </span>
                        <span className="text-[10px] text-text-tertiary" title={event.createdAt}>
                          {relativeTime(event.createdAt)}
                        </span>
                      </div>
                      {event.type === 'STATUS_CHANGED' && d?.reason != null && (
                        <p className="text-[11px] text-text-secondary">{s(d.reason)}</p>
                      )}

                      {/* Provider Override — detailed card */}
                      {isProviderOverride && d && (() => {
                        const fromName = s(d.fromProviderName) || null;
                        const toName = s(d.toProviderName) || s(d.providerName) || null;
                        const assignments = Array.isArray(d.assignments) ? d.assignments as Array<Record<string, unknown>> : [];
                        return (
                          <div className="mt-1.5 rounded-lg border border-orange-400/30 bg-orange-50/50 dark:bg-orange-500/5 px-3 py-2.5 space-y-2">
                            {/* Order-level override */}
                            {!d.itemLevel && (
                              fromName && toName ? (
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="inline-flex items-center gap-1 rounded-md bg-surface px-2 py-0.5 border border-border font-medium text-text-primary">
                                    {fromName}
                                  </span>
                                  <svg className="h-4 w-4 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                  </svg>
                                  <span className="inline-flex items-center gap-1 rounded-md bg-orange-100 dark:bg-orange-500/15 px-2 py-0.5 border border-orange-300/50 font-semibold text-orange-700 dark:text-orange-400">
                                    {toName}
                                  </span>
                                </div>
                              ) : (
                                <p className="text-xs text-text-primary">
                                  Assigned to{' '}
                                  <span className="inline-flex items-center gap-1 rounded-md bg-orange-100 dark:bg-orange-500/15 px-2 py-0.5 border border-orange-300/50 font-semibold text-orange-700 dark:text-orange-400">
                                    {toName || '—'}
                                  </span>
                                </p>
                              )
                            )}
                            {/* Item-level override */}
                            {Boolean(d.itemLevel) && assignments.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">
                                  {assignments.length} item{assignments.length !== 1 ? 's' : ''} reassigned
                                </p>
                                {assignments.map((a, i) => (
                                  <div key={i} className="rounded-md border border-border/50 bg-surface/50 px-2.5 py-1.5 space-y-1">
                                    <p className="text-xs font-medium text-text-primary leading-snug truncate">
                                      {s(a.itemTitle) || 'Unknown item'}
                                      {a.itemSku != null && (
                                        <span className="ml-1.5 font-mono text-[10px] text-text-tertiary bg-surface-secondary px-1 py-px rounded">
                                          {s(a.itemSku)}
                                        </span>
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      {a.fromProviderName ? (
                                        <>
                                          <span className="inline-flex items-center rounded-md bg-surface px-2 py-0.5 border border-border font-medium text-text-primary text-[11px]">
                                            {s(a.fromProviderName)}
                                          </span>
                                          <svg className="h-3.5 w-3.5 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                          </svg>
                                        </>
                                      ) : (
                                        <span className="text-[11px] text-text-tertiary">Assigned to</span>
                                      )}
                                      <span className="inline-flex items-center rounded-md bg-orange-100 dark:bg-orange-500/15 px-2 py-0.5 border border-orange-300/50 font-semibold text-orange-700 dark:text-orange-400 text-[11px]">
                                        {s(a.providerName)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {d.reason != null && (
                              <p className="text-[11px] text-text-secondary">
                                <span className="font-medium text-text-tertiary">Reason:</span> {s(d.reason)}
                              </p>
                            )}
                            {event.userName && (
                              <p className="text-[10px] text-text-tertiary">by {event.userName}</p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Shipment events — carrier/tracking detail */}
                      {isShipmentEvent && d && (
                        <ShipmentEventDetail data={d} />
                      )}

                      {/* Provider failed — action/reason */}
                      {isProviderFailed && d && (
                        <div className="mt-1 rounded-md border border-red-200/50 bg-red-50/30 dark:bg-red-500/5 px-2.5 py-1.5 text-[11px] space-y-0.5">
                          {d.action != null && <p>Action: <span className="font-medium">{s(d.action)}</span></p>}
                          {d.reason != null && <p>Reason: {s(d.reason)}</p>}
                        </div>
                      )}

                      {/* Item cancelled — items list */}
                      {isItemCancelled && d && Array.isArray(d.items) && (
                        <div className="mt-1 rounded-md border border-red-200/50 bg-red-50/30 dark:bg-red-500/5 px-2.5 py-1.5 text-[11px] space-y-0.5">
                          {(d.items as Array<Record<string, unknown>>).map((ci, idx) => (
                            <p key={idx}>
                              Item cancelled: qty {s(ci.cancelQty)}
                              {ci.reason != null && <span className="text-text-tertiary"> — {s(ci.reason)}</span>}
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Other events */}
                      {d && !isNote && !isProviderOverride && !isShipmentEvent && !isProviderFailed && !isItemCancelled && (
                        <div className="text-[11px] text-text-secondary leading-relaxed space-y-1">
                          {d.from != null && d.to != null && (
                            <span className="flex items-center gap-1">
                              <Badge variant="default" size="sm">{s(d.from)}</Badge>
                              <span className="text-text-tertiary">→</span>
                              <Badge variant="default" size="sm">{s(d.to)}</Badge>
                            </span>
                          )}
                          {d.reason != null && <p className="text-text-tertiary">Reason: {s(d.reason)}</p>}
                          {d.providerName != null && <p>Provider: <span className="font-medium">{s(d.providerName)}</span></p>}
                          {d.fulfillmentType != null && (
                            <p>Fulfillment: <span className="font-medium">{s(d.fulfillmentType)}</span></p>
                          )}
                          {d.body != null && (
                            <p className="italic text-text-tertiary">&quot;{s(d.body)}&quot;</p>
                          )}
                          {d.tag != null && (
                            <span className="inline-block rounded-full border border-border bg-surface-secondary px-1.5 py-px text-[10px]">
                              {s(d.tag)}
                            </span>
                          )}
                        </div>
                      )}
                      {isNote && d?.body != null && (
                        <p className="text-[11px] text-text-secondary italic">&quot;{s(d.body)}&quot;</p>
                      )}
                      {event.userName && !isProviderOverride && (
                        <p className="text-[10px] text-text-tertiary">by {event.userName}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {hasMore && (
        <div className="pt-2 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDisplayCount((prev) => prev + LOAD_MORE_COUNT)}
          >
            Load more ({events.length - displayCount} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
